import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {
  getKnowledgeFile,
  listFileVersions,
  type ProjectsApiOptions,
} from '../../api/projects';
import { formatDateTime } from '../../utils/formatDate';
import { ContextFileStatusChip } from './ContextFileStatusChip';
import { type ContextFileEntry, type FileVersionEntry } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  metaRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  metaLabel: {
    color: theme.palette.text.secondary,
    minWidth: 96,
  },
  versionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 0),
  },
  versionNum: {
    fontWeight: 600,
    minWidth: 40,
  },
  versionMeta: {
    color: theme.palette.text.secondary,
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
  subtle: {
    color: theme.palette.text.secondary,
  },
}));

type Props = {
  file: ContextFileEntry | null;
  projectId: string;
  apiOptions: ProjectsApiOptions;
  isPreviewMode: boolean;
  canManage: boolean;
  onClose: () => void;
  onUploadNewVersion: (file: ContextFileEntry) => void;
};

export function FileDetailDialog({
  file,
  projectId,
  apiOptions,
  isPreviewMode,
  canManage,
  onClose,
  onUploadNewVersion,
}: Props) {
  const classes = useStyles();
  const { t } = useTranslation('projects');

  const [detail, setDetail] = useState<ContextFileEntry | null>(null);
  const [versions, setVersions] = useState<FileVersionEntry[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>(
    'idle',
  );

  // The effect below is keyed on the file's ID, not the object, so a caller that
  // passes a fresh object for the same file does not refetch and clear the
  // dialog. It still reads the latest object through this ref.
  const fileRef = useRef(file);
  fileRef.current = file;
  const fileId = file?.fileId;

  useEffect(() => {
    const currentFile = fileRef.current;
    // Drop the previous file's data first, so a new file never shows it while
    // its own request is pending or if that request fails.
    setDetail(null);
    setVersions([]);

    if (!currentFile) {
      setStatus('idle');
      return undefined;
    }

    if (isPreviewMode) {
      setDetail(currentFile);
      setVersions([
        {
          versionNumber: currentFile.version,
          status: currentFile.status,
          uploadedBy: currentFile.uploader,
          uploadedAt: new Date().toISOString(),
        },
      ]);
      setStatus('loaded');
      return undefined;
    }

    let cancelled = false;
    setStatus('loading');
    Promise.all([
      getKnowledgeFile(apiOptions, projectId, currentFile.fileId),
      listFileVersions(apiOptions, projectId, currentFile.fileId),
    ])
      .then(([fileDetail, versionList]) => {
        if (cancelled) return;
        setDetail(fileDetail);
        setVersions(versionList);
        setStatus('loaded');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [fileId, isPreviewMode, apiOptions, projectId]);

  // Overlay the detail response on the row that was clicked, so a field the
  // detail response does not carry (or names differently) falls back to the row
  // instead of rendering blank.
  const shown = detail && file ? { ...file, ...detail } : detail ?? file;

  return (
    <Dialog open={!!file} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t('contextFiles.fileDetailsTitle', { name: shown?.displayName ?? '' })}
      </DialogTitle>
      <DialogContent>
        {shown && (
          <Box mb={2}>
            <Box className={classes.metaRow}>
              <Typography variant="body2" className={classes.metaLabel}>
                {t('table.typeTag')}
              </Typography>
              <Typography variant="body2">{shown.fileTypeTag}</Typography>
            </Box>
            <Box className={classes.metaRow}>
              <Typography variant="body2" className={classes.metaLabel}>
                {t('table.status')}
              </Typography>
              <ContextFileStatusChip
                status={shown.status}
                errorReport={shown.errorReport}
              />
            </Box>
            <Box className={classes.metaRow}>
              <Typography variant="body2" className={classes.metaLabel}>
                {t('table.version')}
              </Typography>
              <Typography variant="body2">{shown.version}</Typography>
            </Box>
          </Box>
        )}

        <Divider />

        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            {t('contextFiles.versionHistory')}
          </Typography>

          {status === 'loading' && (
            <Typography variant="body2" className={classes.subtle}>
              {t('contextFiles.versionHistoryLoading', {
                defaultValue: 'Loading…',
              })}
            </Typography>
          )}

          {status === 'error' && (
            <Typography variant="body2" className={classes.errorText}>
              {t('contextFiles.versionHistoryLoadError')}
            </Typography>
          )}

          {status === 'loaded' && versions.length === 0 && (
            <Typography variant="body2" className={classes.subtle}>
              {t('contextFiles.versionHistoryEmpty')}
            </Typography>
          )}

          {status === 'loaded' &&
            versions.map(version => (
              <Box key={version.versionNumber} className={classes.versionRow}>
                <Typography variant="body2" className={classes.versionNum}>
                  {t('contextFiles.versionBadge', { n: version.versionNumber })}
                </Typography>
                <ContextFileStatusChip status={version.status} />
                <Typography variant="body2" className={classes.versionMeta}>
                  {version.uploadedBy?.displayName || '—'}
                </Typography>
                <Typography variant="body2" className={classes.versionMeta}>
                  {formatDateTime(version.uploadedAt)}
                </Typography>
              </Box>
            ))}
        </Box>
      </DialogContent>
      <DialogActions>
        {canManage && shown && (
          <Button color="primary" onClick={() => onUploadNewVersion(shown)}>
            {t('contextFiles.uploadNewVersion')}
          </Button>
        )}
        <Button onClick={onClose}>{t('actions.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
}
